import { useEffect, useMemo, useState } from 'react';

import { Box, Link } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { financeConfigurationAtom, userInfoAtom } from '~atoms';
import { RevenuesService } from '~features/finances';

import { useStyles } from './style';
import { Table, Label } from '../../../../components';
import { isObject, decimalAmount, getFinanceTableHeaders } from '../../../../shared/utility';
import * as actions from '../../../../store/actions';

const service = new RevenuesService();

interface HistoryProps {
	onFetchRevenues?(...args: unknown[]): unknown;
	isSuperAdmin?: boolean;
	revenues?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
}

const History = (props: HistoryProps) => {
	const { onFetchRevenues, revenues, isSuperAdmin } = props;
	const { t } = useTranslation();
	const classes = useStyles();
	const userInfo = useAtomValue(userInfoAtom);
	const financeConfiguration = useAtomValue(financeConfigurationAtom);

	const [pageNumber, setPageNumber] = useState(1);
	const [pageSize, setPageSize] = useState(20);

	const fetchParameters = useMemo(
		() => ({
			page: pageNumber,
			pageSize: pageSize,
			excludeOneOffPayments: financeConfiguration.excludeOneOffPayments,
		}),
		[pageNumber, pageSize, financeConfiguration.excludeOneOffPayments]
	);

	const { data: revenuesData, isLoading: revenuesLoading } = useSWR(
		[service.basePath, fetchParameters],
		([_, args]) => service.getRevenuesUnmapped(args)
	);

	const handlePageChange = (newPage) => {
		setPageNumber(newPage);
	};

	const handlePageSizeChange = (newSize) => {
		setPageSize(newSize);
		setPageNumber(1);
	};

	const statusColor = {
		open: 'default',
		paid: 'success',
	};

	const statusText = {
		open: t('ui.open'),
		paid: t('ui.label.paid'),
	};

	const tableHeader = getFinanceTableHeaders();

	const handlePathName = (start, title) => {
		const path = `/financeManagement/revenue/${start}/${title}/`;
		if (isSuperAdmin) {
			return `${path}organisations/revenue`;
		} else {
			return `${path}partner/${userInfo.organisation.id}/${encodeURIComponent(userInfo.organisation.label)}`;
		}
	};

	const tableBody =
		revenuesData?.results.map((history) => [
			{
				content: (
					<Box className={classes.cell}>
						<Link
							color='inherit'
							component={RouterLink}
							to={handlePathName(history.start, history.title)}
						>
							{`${history.title}`}
						</Link>
					</Box>
				),
			},
			{ content: `${decimalAmount(history.amount.subtotal, history.amount)}` },
			{ content: `${decimalAmount(history.amount.discount, history.amount)}` },
			{
				content: `${decimalAmount(history.amount.subtotal - history.amount.discount, history.amount)}`,
			},
			{ content: `${decimalAmount(history.amount.priceVat, history.amount)}` },
			{ content: `${decimalAmount(history.amount.refund, history.amount)}` },
			{ content: `${decimalAmount(history.amount.totalPrice, history.amount)}` },
			{ content: `${decimalAmount(history.open, history.amount)}` },
			{
				content: (
					<Box>
						<Label type={statusColor[history.status]}>{statusText[history.status]}</Label>
					</Box>
				),
			},
		]) ??
		Array(5)
			.fill(Array(tableHeader.length).fill())
			.map((arr) => arr.map(() => ({ loading: true })));

	return (
		<Box pt={4.3}>
			<Table
				body={tableBody}
				cellStyle={classes.cellStyle}
				data={revenuesData ? revenuesData.results : []}
				handlePageChange={handlePageChange}
				header={tableHeader}
				loading={revenuesLoading}
				page={pageNumber}
				rowsPerPage={pageSize}
				setRowsPerPage={handlePageSizeChange}
				title={t('views.tableResults.revenue.table.title')}
				total={revenuesData ? revenuesData.total : 0}
				withoutPages={true}
			/>
		</Box>
	);
};

const mapStateToProps = (state) => {
	return {
		revenues: state.paged.revenues,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchRevenues: (page, filters) => dispatch(actions.fetchRevenues(page, filters)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(History);
